/**
 * MKS-393 — the "Size chart" link shown next to a merch size field.
 *
 * Renders NOTHING when the item has no chart. That is the whole contract and
 * it is tested: an item nobody has uploaded a chart for must not show a dead
 * link, and must not show a disabled or greyed-out one either — there is no
 * affordance at all.
 *
 * How it opens, decided from the chart's own content type via
 * `opensInNewTab` rather than re-sniffing the file name here:
 *   - PDF  → a new tab, the browser's own sandboxed viewer;
 *   - PNG/JPG → the shared `ImageLightbox` overlay.
 *
 * The signed URL is fetched on CLICK, not on render. It is short-lived, so
 * asking for four of them on every /profile visit would mostly mint URLs
 * that expire unused — and it would leak "this page was opened" into the
 * bucket's access log four times per visit.
 */
import { useState } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ImageLightbox } from '../common/ImageLightbox';
import { useMerchSizeChartUrl } from '../../hooks/useMerchSizeCharts';
import {
  MERCH_CHART_ITEM_LABELS,
  merchChartItemsForCut,
  merchChartLinkLabel,
  opensInNewTab,
  type MerchChartGarment,
  type MerchChartItem,
  type MerchChartSummary,
} from '../../constants/merchSizeCharts';

interface SizeChartLinkProps {
  item: MerchChartItem;
  /** Every chart that exists; the link renders only if `item` is among them. */
  charts: MerchChartSummary[] | undefined;
  /**
   * The link's text. Defaults to the plain "Size chart", and is overridden
   * only where two charts sit beside one field and the person has to tell
   * them apart (`merchChartLinkLabel`).
   */
  label?: string;
}

/**
 * The existence check lives in this outer component, which uses NO hooks, so
 * an item with no chart mounts nothing at all. That is not only tidier than
 * an early return after the hooks: `useMerchSizeChartUrl` reaches
 * `useErrorToast`, which throws outside a `<ToastHost>` — so a hook above the
 * early return would drag a toast provider into every test that renders a
 * merch size field, chart or no chart.
 */
export function SizeChartLink({ item, charts, label = 'Size chart' }: SizeChartLinkProps) {
  const chart = charts?.find((candidate) => candidate.item === item);
  if (!chart) return null;
  return <ChartOpener item={item} chart={chart} label={label} />;
}

/**
 * The chart links beside a T-shirt or hoodie size field, which are the two
 * garments with a men's and a women's chart.
 *
 * The cut the person picked chooses the chart: women → the women's table,
 * men (and unisex, which is the men's block) → the men's. Before they have
 * picked, there is nothing to choose by, so both are offered and each is
 * labelled by cut; `SizeChartLink` still drops any whose chart HR has not
 * uploaded, so an empty slot leaves no link behind.
 */
export function GarmentSizeChartLinks({
  garment,
  cut,
  charts,
}: {
  garment: MerchChartGarment;
  cut: 'men' | 'women' | null | undefined;
  charts: MerchChartSummary[] | undefined;
}) {
  const items = merchChartItemsForCut(garment, cut);
  const ambiguous = items.length > 1;
  return (
    <span className="flex items-baseline gap-2">
      {items.map((item) => (
        <SizeChartLink
          key={item}
          item={item}
          charts={charts}
          label={merchChartLinkLabel(item, ambiguous)}
        />
      ))}
    </span>
  );
}

function ChartOpener({
  item,
  chart,
  label,
}: {
  item: MerchChartItem;
  chart: MerchChartSummary;
  label: string;
}) {
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);
  const openChart = useMerchSizeChartUrl();

  const itemLabel = MERCH_CHART_ITEM_LABELS[item];

  async function handleClick() {
    // A popup blocker only trusts a window opened synchronously inside the
    // click handler, so the tab for a PDF is opened BEFORE the await and
    // pointed at the URL once it arrives. `noopener` is set on the handle
    // the same way `rel="noopener"` would be on an anchor.
    const pdfTab = opensInNewTab(chart.contentType) ? window.open('', '_blank', 'noopener') : null;

    try {
      const { data } = await openChart.mutateAsync(item);
      if (pdfTab) {
        pdfTab.location.href = data.url;
      } else {
        setLightboxUrl(data.url);
      }
    } catch {
      // `useMerchSizeChartUrl` already surfaced the toast; just don't leave
      // an orphaned blank tab sitting there.
      pdfTab?.close();
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        disabled={openChart.isPending}
        className="inline-flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 hover:underline disabled:opacity-50"
      >
        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
        {label}
      </button>

      {lightboxUrl && (
        <ImageLightbox
          src={lightboxUrl}
          alt={`${itemLabel} size chart`}
          onClose={() => setLightboxUrl(null)}
          testId={`size-chart-lightbox-${item}`}
        />
      )}
    </>
  );
}
